import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import { getRandomCharacters } from '../../tools/general';
import { createAnimationTick } from '../../tools/animationTick';
import { SCHEME_TRANSITION, SCHEME_TRANSFORM, RANDOM_CHARACTERS } from './Text.constants';

class Component extends React.PureComponent {
  static displayName = 'Text';

  static propTypes = {
    theme: PropTypes.object.isRequired,
    classes: PropTypes.object.isRequired,
    energy: PropTypes.object.isRequired,
    audio: PropTypes.object.isRequired,
    sounds: PropTypes.object.isRequired,
    className: PropTypes.any,
    children: PropTypes.string.isRequired,
    scheme: PropTypes.oneOf([SCHEME_TRANSITION, SCHEME_TRANSFORM]),
    randomCharacters: PropTypes.string,
    stableTime: PropTypes.bool,
    typingDuration: PropTypes.number
  };

  static defaultProps = {
    children: '',
    scheme: SCHEME_TRANSITION,
    randomCharacters: RANDOM_CHARACTERS
  };

  componentWillUnmount () {
    this.cancelAnimate();
  }

  enter () {
    this.animate(true);
  }

  exit () {
    this.animate(false);
  }

  cancelAnimate () {
    if (this.animationTick) {
      this.animationTick.cancel();
      this.animationTick = null;
    }
  }

  playSound () {
    const { energy, sounds } = this.props;

    if (energy.animate && sounds.typing && !sounds.typing.playing()) {
      sounds.typing.play();
    }
  }

  stopSound () {
    const { sounds } = this.props;

    if (sounds.typing) {
      sounds.typing.stop();
    }
  }

  getTextString () {
    const { children } = this.props;
    if (typeof children === 'string') return children;
    if (Array.isArray(children)) {
      return children.map(c => {
        if (typeof c === 'string') return c;
        if (typeof c === 'number') return String(c);
        return '';
      }).join('');
    }
    if (children == null) return '';
    if (typeof children === 'number') return String(children);
    return '';
  }

  animate (isEntering) {
    const { scheme } = this.props;
    const text = this.getTextString();

    if (text.length === 0) {
      return;
    }

    this.cancelAnimate();

    if (scheme === SCHEME_TRANSITION) {
      this.animateTransition(isEntering);
    } else {
      this.animateTransform(isEntering);
    }
  }

  getDuration () {
    const { theme, stableTime, typingDuration, energy } = this.props;
    const text = this.getTextString();
    const animationDuration = energy && energy.duration && energy.duration.enter;
    const durationLimit = typingDuration || animationDuration || theme.animation.time;

    if (stableTime) {
      return durationLimit;
    }

    // requestAnimationFrame uses 60 FPS.
    // The time it will take to add/remove a character per frame for
    // the actual text.
    const realDuration = (1000 / 60) * text.length;

    // Animation duration will be at most the configured animation duration.
    const duration = Math.min(realDuration, durationLimit);

    return duration;
  }

  animateTransition (isEntering) {
    const duration = this.getDuration();
    const isInverted = !isEntering;
    const text = this.getTextString();

    this.setOverlayText(isEntering ? '' : text);

    const onCall = ({ timeProgress }) => {
      // If:
      // progressLength(n) = progressDuration(ms)
      // totalLength(n) = totalDuration(ms)
      // Then:
      const newLength = Math.round((timeProgress * text.length) / duration);

      const newText = text.substring(0, newLength);
      this.setOverlayText(newText);

      this.playSound();

      return isEntering ? newLength <= text.length : newLength > 0;
    };

    const onDone = () => this.stopSound();

    this.animationTick = createAnimationTick({ duration, isInverted, onCall, onDone });
  }

  animateTransform (isEntering) {
    const { randomCharacters } = this.props;
    const duration = this.getDuration();
    const isInverted = !isEntering;
    const text = this.getTextString();

    const initialText = getRandomCharacters(text.length, randomCharacters);
    this.setOverlayText(initialText);

    const onCall = ({ timeProgress }) => {
      // If:
      // progressLength(n) = progressDuration(ms)
      // totalLength(n) = totalDuration(ms)
      // Then:
      const newLength = Math.round((timeProgress * text.length) / duration);

      const newText1 = text.substring(0, newLength);
      const newText2 = getRandomCharacters(text.length - newLength, randomCharacters);
      this.setOverlayText(newText1 + newText2);

      this.playSound();

      return isEntering ? newLength <= text.length : newLength > 0;
    };

    const onDone = () => {
      if (isInverted) {
        this.setOverlayText('');
      }

      this.stopSound();
    };

    this.animationTick = createAnimationTick({ duration, isInverted, onCall, onDone });
  }

  setOverlayText (text) {
    if (this.overlayTextElement) {
      this.overlayTextElement.textContent = text;
    }
  }

  render () {
    const {
      theme,
      classes,
      energy,
      audio,
      sounds,
      className,
      children,
      scheme,
      randomCharacters,
      stableTime,
      typingDuration,
      ...etc
    } = this.props;
    const animating = energy.entering || energy.exiting;

    const cls = cx(
      classes.root,
      {
        [classes.hidden]: energy.animate && !energy.show && !animating,
        [classes.animating]: animating
      },
      className
    );

    return (
      <span className={cls} {...etc}>
        <span className={classes.actualText}>{children}</span>
        {animating && (
          <span className={classes.overlay}>
            <span
              className={classes.overlayText}
              ref={ref => (this.overlayTextElement = ref)}
            />
          </span>
        )}
      </span>
    );
  }
}

export { Component };

// Commit trace: 2025-11-05T14:41:31 - chore: update formatting in Text.js

// Commit trace: 2026-01-06T23:44:42 - style: fix spacing in Text.js

// Commit trace: 2026-04-08T04:07:37 - feat: add utility helper to Text.js
